import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiCall } from "../utils/api";
import toast, { Toaster } from 'react-hot-toast';

function Login() {
  const navigate = useNavigate();

  // Login states
  const [mobile, setMobile] = useState("");
  const [error, setError] = useState("");
  const [loading, setIsLoading] = useState(false);

  // OTP states
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [otpToken, setOtpToken] = useState("");
  const inputRefs = useRef([]);

  const maskedMobile = mobile
    ? mobile.slice(0, 2) + "****" + mobile.slice(6)
    : "XXXXXXXXXX";

  // OTP Timer effect
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  // Focus first OTP input when shown
  useEffect(() => {
    if (showOtp && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [showOtp]);

  // Handle mobile input change
  const handleMobileChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
    setMobile(val);
    if (error) setError("");
  };

  // Handle mobile submit
  const handleMobileSubmit = async (e) => {
    e.preventDefault();

    if (mobile.length !== 10) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }

    setIsLoading(true);

    try {
      const result = await apiCall("login", { number: mobile }, false);
      if (result.status === true) {
        setOtpToken(result.data.otp_token);
        setShowOtp(true);
        setTimer(30);
        setCanResend(false);
        toast.success("OTP sent successfully!", { position: 'top-right' });
      } else if (result.response_code === 3) {
        // User not registered - show toast and stay on login
        toast.error("Mobile number not registered. Please contact support.", { position: 'top-right' });
      } else {
        toast.error(result.message || "An error occurred. Please try again.", { position: 'top-right' });
      }
    } catch (error) {
      toast.error("Network error. Please try again.", { position: 'top-right' });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle OTP verification
  const handleVerify = useCallback(async () => {
    const code = otp.join("");
    if (code.length !== 6) {
      toast.error("Please enter complete 6-digit OTP", { position: 'top-right' });
      return;
    }

    setVerifying(true);

    try {
      const result = await apiCall(
        "verify-otp",
        {
          number: mobile,
          otp: code,
          otp_token: otpToken
        },
        false
      );

      if (result.status === true) {
        toast.success("OTP verified successfully!", { position: 'top-right' });
        localStorage.setItem("credentials", JSON.stringify(result.data));
        navigate("/", { replace: true });
      } else {
        toast.error(result.message || "Invalid OTP. Please try again.", { position: 'top-right' });
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      toast.error("Network error. Please try again.", { position: 'top-right' });
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  }, [otp, mobile, otpToken, navigate]);

  // Auto-verify OTP when complete
  useEffect(() => {
    const code = otp.join("");
    if (code.length === 6 && !verifying && showOtp) {
      const timeout = setTimeout(() => {
        handleVerify();
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [otp, handleVerify, verifying, showOtp]);

  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    if (verifying) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle OTP keydown
  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle OTP paste
  const handleOtpPaste = (e) => {
    if (verifying) return;
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pastedData.length > 0) {
      const newOtp = [...otp];
      for (let i = 0; i < 6; i++) {
        newOtp[i] = pastedData[i] || "";
      }
      setOtp(newOtp);
      const focusIndex = Math.min(pastedData.length, 5);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  // Handle resend OTP
  const handleResend = async () => {
    if (!canResend || verifying || resending) return;

    setResending(true);

    try {
      const result = await apiCall(
        "login",
        { number: mobile },
        false
      );

      if (result.status === true) {
        setOtpToken(result.data.otp_token);
        toast.success("OTP resent successfully!", { position: 'top-right' });
        setTimer(30);
        setCanResend(false);
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      } else {
        toast.error(result.message || "Failed to resend OTP. Please try again.", { position: 'top-right' });
      }
    } catch (error) {
      toast.error("Network error. Please try again.", { position: 'top-right' });
    } finally {
      setResending(false);
    }
  };

  // Handle back to mobile input
  const handleBack = () => {
    if (!verifying && !loading) {
      setShowOtp(false);
      setOtp(["", "", "", "", "", ""]);
      setError("");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-600 shadow-md">
            <span className="text-[28px] font-bold text-white">A</span>
          </div>
        </div>

        {!showOtp ? (
          // Mobile Number Input Section
          <>
            <h1 className="text-2xl font-bold mb-2 text-center text-gray-900">
              Login In
            </h1>

            <p className="text-sm mb-8 text-center text-gray-600">
              Enter your mobile number to continue
            </p>

            <form onSubmit={handleMobileSubmit} className="w-full">
              <div
                className={`flex items-center overflow-hidden transition-colors duration-200 border rounded-lg ${error ? 'border-red-500' : 'border-gray-300'
                  }`}
              >
                <div className="px-3.5 py-3 text-sm font-semibold whitespace-nowrap bg-gray-50 text-gray-700 border-r border-gray-300">
                  +91
                </div>
                <input
                  type="tel"
                  maxLength={10}
                  pattern="[0-9]*"
                  placeholder="10-digit mobile number"
                  value={mobile}
                  onChange={handleMobileChange}
                  disabled={loading}
                  className="flex-1 px-3.5 py-3 border-none text-sm outline-none bg-transparent disabled:opacity-60 text-gray-900"
                />
              </div>
              {error && (
                <div className="text-xs mt-1.5 text-red-500">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 border-none rounded-lg text-[15px] font-semibold mt-5 transition-all duration-150 disabled:opacity-70 disabled:cursor-not-allowed bg-blue-600 hover:bg-blue-700 text-white"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 rounded-full animate-spin border-2 border-white border-t-transparent" />
                    Sending...
                  </div>
                ) : (
                  "Send OTP"
                )}
              </button>
            </form>
          </>
        ) : (
          // OTP Verification Section
          <>
            <button
              onClick={handleBack}
              disabled={verifying || loading}
              className="bg-transparent w-10 h-10 rounded-full flex items-center justify-center mb-4 transition-transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 hover:bg-gray-50"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
            </button>

            <h1 className="text-2xl font-bold mb-2 text-center text-gray-900">
              Verify OTP
            </h1>

            <p className="text-sm mb-8 text-center text-gray-600">
              OTP sent to +91 {maskedMobile}
            </p>

            <div className="w-full">
              <div className="rounded-2xl p-8 pb-6 mb-6 flex flex-col items-center bg-gray-50 border border-gray-200">
                <div
                  className="flex gap-2 mb-6 justify-center w-full"
                  onPaste={handleOtpPaste}
                >
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="tel"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      disabled={verifying}
                      className="w-12 h-14 text-center text-xl font-semibold rounded-lg outline-none transition-all duration-200 disabled:opacity-60 border-2 focus:border-blue-500 focus:shadow-md"
                      style={{
                        borderColor: digit ? '#2563eb' : '#d1d5db',
                        color: '#111827',
                        background: '#ffffff',
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#2563eb';
                        e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = digit ? '#2563eb' : '#d1d5db';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  ))}
                </div>

                <div className="w-full text-sm text-gray-600">
                  {canResend ? (
                    <span
                      onClick={handleResend}
                      className={`font-semibold ${(verifying || resending) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:text-blue-700'
                        }`}
                      style={{ color: '#2563eb' }}
                    >
                      {resending ? "Sending..." : "Resend OTP"}
                    </span>
                  ) : (
                    <span>
                      Resend OTP in <span className="font-semibold text-blue-600">{timer}s</span>
                    </span>
                  )}
                </div>
              </div>

              <button
                onClick={handleVerify}
                disabled={verifying || otp.join("").length !== 6}
                className="w-full h-12 border-none rounded-lg text-[15px] font-semibold transition-all duration-150 disabled:opacity-70 disabled:cursor-not-allowed bg-blue-600 hover:bg-blue-700 text-white"
              >
                {verifying ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 rounded-full animate-spin border-2 border-white border-t-transparent" />
                    Verifying...
                  </div>
                ) : (
                  "Verify OTP"
                )}
              </button>
            </div>
          </>
        )}

        {/* Footer section */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="text-xs text-center leading-relaxed text-gray-500">
            By continuing you agree to our{" "}
            <span
              onClick={() => (!loading && !verifying) && navigate("/terms")}
              className={`inline-block text-blue-600 hover:text-blue-700 ${(loading || verifying) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                }`}
            >
              Terms
            </span>{" "}
            &{" "}
            <span
              onClick={() => (!loading && !verifying) && navigate("/privacy")}
              className={`inline-block text-blue-600 hover:text-blue-700 ${(loading || verifying) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                }`}
            >
              Privacy
            </span>
          </div>
        </div>
        <Toaster position="top-right" />
      </div>
    </div>
  );
}

export default Login;